import React, { useState } from 'react';
import repositoryCss from './repository.css';
import {
    IconTextButton,
    AdvanceTable,
    IBasicTableProps,
    IAdvanceTableToolbarProps,
    notification,
} from 'ui-lib';
import { useAccountStore, useAuthStore, useRepositoriesStore, useStores } from 'store';
import { TableUtils } from 'pages/private/components/table-utils';
import { confirmAction } from 'pages/private/admin/utils';
import { useAccountTreeSelectDataSource } from 'pages/private/admin/accounts';
import { AccountType } from 'dto/access-management/account-dto';
import { OrganisationSelect } from 'pages/private/components/organisation-select';
import { IRepositoryDetailProps, ISubmitProps, RepositoryDetail } from './repository-detail';
import { FILTER_REPOSITORY_TYPE, RepositoryDto } from 'dto/repository.dto';
import { getErrorMessage } from 'common-utils';
import { useUrlSearch } from 'hooks/use-search.hook';
import { observer } from 'mobx-react';
import { useRepositoryPermissionContext } from 'contexts/permissions/repository-permission.context';

export interface IRepositoryDetailState {
    [key: string]: IRepositoryDetailProps;
}

export enum IRepositoryComponent {
    LIST = 'LIST',
    DETAIL = 'DETAIL',
}

export const RepositoryTab = observer(() => {
    const [state, setState] = useState<IRepositoryComponent>(IRepositoryComponent.LIST);
    const { currentUser } = useAuthStore();
    const stores = useStores();
    const accountStore = useAccountStore();
    const repositoriesStore = useRepositoriesStore();
    const selectAccountSource = useAccountTreeSelectDataSource({
        selectableTypes: [AccountType.DPU, AccountType.VAR, AccountType.CLIENT],
    });
    const [reloading, setReloading] = React.useState<boolean>(false);
    const [repositoriesSource, setRepositoriesSource] = React.useState<RepositoryDto[]>([]);
    const [selectRepository, setSelectRepository] = React.useState<RepositoryDto>(null);

    const { getSearchParam, updateSearchParams } = useUrlSearch();

    const { canModify: hasModifyPermission } = useRepositoryPermissionContext();
    const defaultAccountUuid = getSearchParam('accountUuid');
    const defaultAccountId = accountStore.getAccountByUuid(defaultAccountUuid)?.id || currentUser.accountId;
    const [selectedAccountId, setSelectedAccountId] = React.useState<number>(defaultAccountId);
    const fullPermission = React.useMemo<boolean>(() => {
        return currentUser.accountId === selectedAccountId;
    }, [selectedAccountId]);

    const OnDeleteButtonClick = async (repository: RepositoryDto) => {
        confirmAction(undefined, 'Delete Repository', `Are you sure you want to delete the repository?`, async () => {
            try {
                await repositoriesStore.deleteRepository(currentUser.accountId, repository.uuid);
                setRepositoriesSource((prev) => prev.filter((item) => item.uuid !== repository.uuid));
                notification.success({
                    message: 'Repository Successfully Deleted',
                    description: `Repository ${repository.label} has been deleted.`,
                });
            } catch (error) {
                notification.error({
                    message: 'Delete repository error',
                    description: getErrorMessage(error),
                });
            }
        });
    };

    const ActionComponent = (props: { clientAccountId: number; record: RepositoryDto }): JSX.Element => {
        const repos: RepositoryDto = props.record;
        return (
            <div className={repositoryCss.actionButtons}>
                <IconTextButton
                    className={repositoryCss.linkButton}
                    key='repositoryEditButton'
                    type='link'
                    label='Edit'
                    disabled={!fullPermission || repos.isSystem}
                    onClick={() => {
                        setSelectRepository(repos);
                        setState(IRepositoryComponent.DETAIL);
                    }}
                />
                <IconTextButton
                    className={repositoryCss.linkButton}
                    key='repoitoryDeleteButton'
                    type='link'
                    label='Delete'
                    disabled={!fullPermission || repos.isSystem}
                    onClick={() => {
                        OnDeleteButtonClick(repos);
                    }}
                />
            </div>
        );
    };

    const repositoryTable: IBasicTableProps = {
        dataSource: repositoriesSource,
        columns: [
            TableUtils.createBasicFilterColumn({ title: 'Repository', key: 'label' }),
            TableUtils.createBasicEnumFilterColumn({
                title: 'Type',
                key: 'type',
                filters: Object.keys(FILTER_REPOSITORY_TYPE).map((type) => ({
                    text: FILTER_REPOSITORY_TYPE[type],
                    value: type,
                })),
            }),
            TableUtils.createBasicFilterColumn({ title: 'Description', key: 'description' }),
            TableUtils.createBasicFilterTimeColumn({
                key: 'updatedAt',
                title: 'Updated At',
            }),
            ...(hasModifyPermission
                ? [
                      TableUtils.createExtraColumn({
                          key: 'actions',
                          title: 'Actions',
                          render: (text: string, record: RepositoryDto) => (
                              <ActionComponent clientAccountId={selectedAccountId} record={record} />
                          ),
                      }),
                  ]
                : []),
        ],
    };

    const onAddRepository = () => {
        setSelectRepository(null);
        setState(IRepositoryComponent.DETAIL);
    };

    const onReloadData = async () => {
        setReloading(true);
        try {
            setRepositoriesSource([]);
            if (selectedAccountId) {
                const selectAccount = accountStore.findAccount(selectedAccountId);
                const repositories = await repositoriesStore.getRepositories(currentUser.accountId, selectAccount.uuid);
                setRepositoriesSource(repositories);
            }
        } catch (error) {
            notification.error({
                message: 'Error',
                description: `Failed to load repositories: ${getErrorMessage(error)}`,
            });
        }
        setReloading(false);
    };

    const repositoryTableToolbar: IAdvanceTableToolbarProps = {
        onAdd: hasModifyPermission ? onAddRepository : undefined,
        onReload: onReloadData,
    };

    React.useEffect(() => {
        (async () => {
            await onReloadData();
        })();
    }, [selectedAccountId]);

    const onRepositoryDetailClose = async () => {
        setState(IRepositoryComponent.LIST);
        await onReloadData();
    };

    const repositoryDetailState: IRepositoryDetailState = {
        NEW: {
            label: 'Create New Repository',
            submitButtonLabel: 'Create',
            repositoryTypeSelectAble: true,
            onSubmit: (props: ISubmitProps) =>
                confirmAction(
                    props,
                    'Create Repository',
                    'Do you want to create this repository?',
                    async (props: ISubmitProps) => {
                        try {
                            const result = await repositoriesStore.createRepository(
                                currentUser.accountId,
                                currentUser.accountUuid,
                                {
                                    label: props.label,
                                    type: props.type,
                                    description: props.description,
                                },
                            );

                            if (result) {
                                notification.success({
                                    message: 'Repository Successfully Created',
                                    description: `Repository ${props.label} has been created.`,
                                });
                            }
                            onRepositoryDetailClose();
                        } catch (err) {
                            notification.error({
                                message: getErrorMessage(err),
                                placement: 'topRight',
                            });
                        }
                    },
                ),
            onClose: onRepositoryDetailClose,
        },
        EDIT: {
            repository: selectRepository,
            label: 'Edit Repository',
            submitButtonLabel: 'Update',
            repositoryTypeSelectAble: false,
            onSubmit: (props: ISubmitProps) =>
                confirmAction(
                    props,
                    'Update Repository',
                    'Do you want to update this repository?',
                    async (props: ISubmitProps) => {
                        try {
                            const result = await repositoriesStore.updateRepository(
                                currentUser.accountId,
                                selectRepository.uuid,
                                {
                                    label: props.label,
                                    description: props.description,
                                },
                            );

                            if (result) {
                                notification.success({
                                    message: 'Repository Successfully Updated',
                                    description: `Repository ${props.label} has been updated.`,
                                });
                            }
                            onRepositoryDetailClose();
                        } catch (err) {
                            notification.error({
                                message: getErrorMessage(err),
                                placement: 'topRight',
                            });
                        }
                    },
                ),
            onClose: onRepositoryDetailClose,
        },
    };
    return (
        <React.Fragment>
            {state === IRepositoryComponent.LIST && currentUser.accountType != AccountType.CLIENT && (
                <OrganisationSelect
                    placeholder='Organisation'
                    treeDataSource={selectAccountSource}
                    expandAll={true}
                    value={selectedAccountId}
                    onChange={(value) => {
                        stores.commonStore.setSelectedOrganisation(value);
                        setSelectedAccountId(value);
                        const selectedAccount = accountStore.getAccountById(value);
                        selectedAccount && updateSearchParams({ accountUuid: selectedAccount.uuid });
                    }}
                />
            )}
            {state === IRepositoryComponent.LIST && (
                <AdvanceTable
                    toolbar={repositoryTableToolbar}
                    title={'Repositories'}
                    table={repositoryTable}
                    loadingState={reloading}
                />
            )}
            {state === IRepositoryComponent.DETAIL && (
                <RepositoryDetail {...repositoryDetailState[selectRepository ? 'EDIT' : 'NEW']} />
            )}
        </React.Fragment>
    );
});